import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="contact_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    try {
      const formData = new FormData(form);
      formData.delete('contact_alt');
      const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      await fetch('https://readdy.ai/api/form/d8vu386s51g94qljs2v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded,
      });
      setFormStatus('success');
      setEmail('');
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="px-6 md:px-10 py-16">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 mb-14">
            <div className="lg:w-[30%]">
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 text-sm font-bold">
                  <i className="ri-leaf-line"></i>
                </span>
                <span className="text-[15px] font-semibold text-background-50">itaobuy.hair</span>
              </Link>
              <p className="text-[13px] text-foreground-400 leading-relaxed max-w-[260px]">
                Your trusted gateway to smarter spreadsheet-based product comparison and informed online shopping decisions.
              </p>
            </div>

            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-8">
              <div>
                <h4 className="text-[13px] font-semibold text-background-50 mb-4">Guides</h4>
                <div className="flex flex-col gap-2.5">
                  <Link to="/itaobuy-spreadsheet-guide" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Spreadsheet Guide</Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">How It Works</a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Getting Started</a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Tips & Tricks</a>
                </div>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold text-background-50 mb-4">Categories</h4>
                <div className="flex flex-col gap-2.5">
                  {categories.slice(0, 6).map((cat) => (
                    <a key={cat.seq} href={cat.url} target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold text-background-50 mb-4">More Categories</h4>
                <div className="flex flex-col gap-2.5">
                  {categories.slice(6, 12).map((cat) => (
                    <a key={cat.seq} href={cat.url} target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-[13px] font-semibold text-background-50 mb-4">Legal</h4>
                <div className="flex flex-col gap-2.5">
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Privacy Policy</a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Terms of Use</a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[12px] text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Disclaimer</a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-foreground-800 pt-10 flex flex-col lg:flex-row gap-8 lg:gap-0">
            <div className="lg:w-[40%]">
              <div className="w-full h-40 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20modern%20product%20display%20with%20warm%20neutral%20toned%20fashion%20accessories%20elegantly%20arranged%20on%20light%20wooden%20surface%2C%20soft%20natural%20morning%20lighting%2C%20editorial%20photography%2C%20warm%20beige%20background%2C%20high%20detail%2C%20calm%20sophisticated%20aesthetic%2C%20professional%20product%20styling&width=800&height=400&seq=footer-visual-v4&orientation=landscape"
                  alt="itaobuy.hair product display"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:w-[25%] flex flex-col justify-center">
              <p className="text-[15px] font-heading text-background-50 leading-relaxed italic">
                Smart shopping starts with organized data. Discover the power of structured comparison.
              </p>
            </div>

            <div className="lg:w-[35%] flex flex-col justify-center">
              <p className="text-[11px] text-foreground-400 mb-3">Stay updated with the latest shopping guides and spreadsheet tips.</p>
              <form
                data-readdy-form
                id="newsletter-form"
                onSubmit={handleSubmit}
                className="flex gap-2"
              >
                <input
                  type="text"
                  name="contact_alt"
                  className="contact_alt_field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 h-11 px-4 rounded-xl bg-transparent border border-foreground-700 text-[13px] text-background-50 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="h-11 px-5 rounded-xl bg-primary-500 text-background-50 text-[13px] font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {formStatus === 'submitting' ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : formStatus === 'success' ? (
                    <i className="ri-check-line text-base"></i>
                  ) : (
                    <span>Subscribe <i className="ri-arrow-right-line ml-1 text-xs"></i></span>
                  )}
                </button>
              </form>
              {formStatus === 'success' && (
                <p className="text-[11px] text-primary-400 mt-2">Thank you for subscribing!</p>
              )}
              {formStatus === 'error' && (
                <p className="text-[11px] text-accent-500 mt-2">Something went wrong. Please try again.</p>
              )}
            </div>
          </div>

          <div className="border-t border-foreground-800 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] text-foreground-500">&copy; itaobuy.hair 2026. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[11px] text-foreground-500 hover:text-background-50 transition-colors">Privacy</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[11px] text-foreground-500 hover:text-background-50 transition-colors">Terms</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-[11px] text-foreground-500 hover:text-background-50 transition-colors">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}